"use client";

import {
  Checkbox,
  ListItem,
  ListItemContent,
  ListItemIcon,
  RadioGroupIndicator,
  RadioGroupItem,
  RadioGroupLabel,
} from "@dafiti/closet-ui";
import { Chevron } from "@dafiti/closet-ui/icons";
import { cn } from "@dafiti/closet-ui/utils";
import { useTranslations } from "next-intl";

import type { CategoryChild } from "../../../types/filters.type";

interface CategoryRadioItemProps {
  child: CategoryChild;
  isCompact?: boolean;
  onNavigate: (child: CategoryChild) => void;
}

function CategoryRadioItem({
  child,
  isCompact,
  onNavigate,
}: CategoryRadioItemProps) {
  const t = useTranslations("catalog");
  const hasChildren = child.children != null && child.children.length > 0;

  return (
    <ListItem
      as="div"
      className={cn("w-full p-0", isCompact ? "py-2" : "py-4")}
    >
      <ListItemIcon className={isCompact ? "h-[30px]" : undefined}>
        <RadioGroupItem value={child.id} id={`category-radio-${child.id}`}>
          <RadioGroupIndicator />
        </RadioGroupItem>
      </ListItemIcon>
      <ListItemContent>
        <RadioGroupLabel
          htmlFor={`category-radio-${child.id}`}
          className="text-body-3 lg:text-body-2 cursor-pointer"
        >
          {child.label}
        </RadioGroupLabel>
      </ListItemContent>
      {hasChildren && (
        <button
          type="button"
          onClick={() => onNavigate(child)}
          aria-label={t("facet.navigateTo", { label: child.label })}
          className="flex flex-1 cursor-pointer items-center justify-end self-stretch"
        >
          <Chevron className="size-3 shrink-0 rotate-90 fill-gray-600" />
        </button>
      )}
    </ListItem>
  );
}

interface CategoryCheckboxItemProps {
  id: string;
  label: string;
  checked: boolean;
  isCompact?: boolean;
  onCheckedChange: () => void;
}

function CategoryCheckboxItem({
  id,
  label,
  checked,
  isCompact,
  onCheckedChange,
}: CategoryCheckboxItemProps) {
  return (
    <ListItem
      as="div"
      className={cn("w-full p-0", isCompact ? "py-2" : "py-4")}
    >
      <ListItemIcon className={isCompact ? "h-[30px]" : undefined}>
        <Checkbox
          id={`category-checkbox-${id}`}
          checked={checked}
          onCheckedChange={onCheckedChange}
        />
      </ListItemIcon>
      <ListItemContent>
        <label
          htmlFor={`category-checkbox-${id}`}
          className="text-body-3 lg:text-body-2 cursor-pointer text-gray-500"
        >
          {label}
        </label>
      </ListItemContent>
    </ListItem>
  );
}

export { CategoryRadioItem, CategoryCheckboxItem };
export type { CategoryRadioItemProps, CategoryCheckboxItemProps };
